import Component from '@glimmer/component';
import { service } from '@ember/service';
import type { TOC } from '@ember/component/template-only';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { and, not } from 'tio-ui/utilities';

import { t, type IntlService } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { PhSealCheck, PhCurrencyCircleDollar, PhTrash, PhPencil } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Modal from 'tio-ui/components/modal';
import { VStack } from 'tio-ui/components/layout';
import type CompanySettingModel from 'tio-data/models/company-setting';
import type CompanyLpnSetting from 'tio-data/models/company-lpn-setting';
import type LpnPage from 'tio-data/models/lpn-page';
import type RouterService from '@ember/routing/router-service';
import type StoreService from '@ember-data/store';
import { LinkTo } from '@ember/routing';

interface LpnCardSignature {
  Args: {
    lpn: LpnPage;
    companySettings?: CompanySettingModel;
    isAdminView?: boolean;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

const ConditionalLink: TOC<{
  Args: { href?: string; isAdminView?: boolean };
  Blocks: { default: [] };
}> = <template>
  {{#if (and (not @isAdminView) @href)}}
    <a href={{@href}} target="_blank" rel="noopener noreferrer" class="block h-full no-underline">
      {{yield}}
    </a>
  {{else}}
    {{yield}}
  {{/if}}
</template>;

export default class LpnCard extends Component<LpnCardSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked isDeleteModalOpen = false;
  @tracked isDeleting = false;

  openDeleteModal = () => {
    this.isDeleteModalOpen = true;
  };

  closeDeleteModal = () => {
    this.isDeleteModalOpen = false;
  };

  deletePartner = async () => {
    try {
      this.isDeleting = true;
      this.args.lpn.deleteRecord();
      await this.args.lpn.save();
      this.store.unloadRecord(this.args.lpn);
      this.router.transitionTo('authenticated.lpn.index');
    } catch (error) {
      this.isDeleting = false;
      console.error('Error deleting partner:', error);
    }
  };

  companyLpnSettingState = trackedFunction(this, async () => {
    return this.args.lpn.settingForCompany(this.args.companySettings);
  });

  get companyLpnSetting() {
    return this.companyLpnSettingState.value as CompanyLpnSetting | undefined;
  }

  get mergedLpn() {
    const overrides = (this.companyLpnSetting ?? {}) as Record<string, unknown>;
    return new Proxy(this.args.lpn as LpnPage, {
      get(target, prop: string | symbol) {
        if (typeof prop === 'string') {
          const value = overrides[prop];
          // Only use the company-lpn-setting value if it's set (not null/undefined).
          // null/undefined = "not overridden, inherit from global LPN page".
          // false, 0, '' are valid override values (e.g. deliberately disabling a badge).
          if (value !== null && value !== undefined) {
            return value;
          }
          return (target as unknown as Record<string, unknown>)[prop];
        }
        return (target as unknown as Record<symbol, unknown>)[prop];
      },
    }) as LpnPage;
  }

  /**
   * The "top badge" is the first of capped-tuition / savings-percent / savings-amount
   * that the LPN has. It renders prominently below the logo (next to Employer Pick)
   * and is excluded from the tag cloud to avoid duplication.
   */
  get topBadge() {
    return this.cappedTuitionBadge ?? this.savingsPercentBadge ?? this.savingsAmountBadge ?? null;
  }

  get providedBy() {
    return this.mergedLpn.providedBy?.trim();
  }

  get descriptionHtml() {
    // Use companyLpnSetting description if it has blocks, otherwise fall back to lpnPage description
    const companyBlocks = this.companyLpnSetting?.description?.blocks ?? [];
    const lpnBlocks = this.args.lpn.description?.blocks ?? [];
    const blocks = companyBlocks.length > 0 ? companyBlocks : lpnBlocks;

    const html: string[] = [];
    for (const block of blocks) {
      switch (block.type) {
        case 'paragraph':
          html.push(`<p>${block.data?.text ?? ''}</p>`);
          break;
        case 'header': {
          const level = Math.min(6, Math.max(1, parseInt(block.data?.level) || 3));
          html.push(`<h${level}>${block.data?.text ?? ''}</h${level}>`);
          break;
        }
        case 'list': {
          const tag = block.data?.style === 'ordered' ? 'ol' : 'ul';
          const items = (block.data?.items ?? [])
            .map(
              (item: string | { content?: string }) =>
                `<li>${typeof item === 'string' ? item : (item?.content ?? '')}</li>`
            )
            .join('');
          html.push(`<${tag}>${items}</${tag}>`);
          break;
        }
        default:
          if (block.data?.text) {
            html.push(`<p>${block.data.text}</p>`);
          }
          break;
      }
    }
    return htmlSafe(html.join(''));
  }

  get cappedTuitionBadge() {
    if (!this.mergedLpn.badgeCappedTuition) {
      return null;
    }
    const range = (this.mergedLpn.badgeCappedTuitionRange ?? '').trim();
    if (range.length > 0) {
      return `${this.intl.t('lpn.badges.capped_tuition_at')} $${range}`;
    }
    return this.intl.t('lpn.badges.capped_tuition');
  }

  get savingsPercentBadge() {
    if (this.mergedLpn.useDiscountPercentRange) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercentRange}%`;
    }
    if (this.mergedLpn.badgeDiscount) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercent}%`;
    }
    return null;
  }

  get savingsAmountBadge() {
    if (this.mergedLpn.useSaveDollarAmountRange) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountRange}`;
    }
    if (this.mergedLpn.saveDollarAmount) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountValue}`;
    }
    return null;
  }

  get costPerCreditBadge() {
    if (this.mergedLpn.useCostPerCreditRange) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCreditRange}`;
    }
    if (this.mergedLpn.costPerCredit && this.mergedLpn.costPerCredit.length > 0) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCredit}`;
    }
    return null;
  }
  get tags() {
    const promoted = this.topBadge;
    return [
      // Capped Tuition (skip if promoted to top badge)
      promoted !== this.cappedTuitionBadge ? this.cappedTuitionBadge : null,
      // Savings Percent (skip if promoted to top badge)
      promoted !== this.savingsPercentBadge ? this.savingsPercentBadge : null,
      // Savings Amount (skip if promoted to top badge)
      promoted !== this.savingsAmountBadge ? this.savingsAmountBadge : null,
      // Part Tuition Waived
      this.mergedLpn.partTuitionWaiver ? this.intl.t('lpn.badges.part_tuition_waiver') : null,
      // Low Tuition
      this.mergedLpn.lowTuition ? this.intl.t('lpn.badges.low_tuition') : null,
      // Family Eligible
      this.mergedLpn.badgeFamilySavings ? this.intl.t('lpn.badges.family_eligible') : null,
      // No Fees
      this.mergedLpn.badgeFeesWaived ? this.intl.t('lpn.badges.no_fees') : null,
      // Cost Per Credit
      this.costPerCreditBadge,
      // Formats
      ...(this.mergedLpn.format ?? []),
      // Programs
      ...(this.mergedLpn.programs ?? []),
    ].filter(Boolean);
  }
  <template>
    <ConditionalLink @isAdminView={{@isAdminView}} @href={{this.mergedLpn.url}}>
      <VStack
        @collapsed={{true}}
        class="h-full border tio-border-default rounded-2xl tio-bg-surface p-4 shadow-sm"
      >
        <div class="tio-row-start-between">
          <div class="tio-row">
            {{#if this.mergedLpn.imageUrl}}
              {{! <div class="tio-row-center h-12 w-40 rounded border tio-border-subtle tio-bg-surface"> }}
              <img
                src="{{this.mergedLpn.imageUrl}}"
                alt="{{this.mergedLpn.schoolName}}"
                width="275"
                height="90"
              />
              {{! </div> }}
            {{/if}}
          </div>
          {{#if @isAdminView}}
            <span class="tio-row-center">
              <button
                type="button"
                class="p-1"
                aria-label="Delete learning partner"
                {{on "click" this.openDeleteModal}}
              >
                <PhTrash class="w-6 h-6 hover:cursor-pointer text-red-600" />
              </button>
              <LinkTo
                @route="authenticated.lpn.edit"
                @model={{@lpn.id}}
                aria-label="Edit learning partner"
              >
                <PhPencil class="text-primary-800 w-6 h-6" />
              </LinkTo>
            </span>
          {{/if}}
        </div>

        <div class="flex min-h-[10px] flex-wrap gap-2 text-sm tio-fg-secondary">
          {{#if (and @isAdminView (not @lpn.institutionIdentifier) (not @lpn.dapip_id))}}
            <Chip @appearance="outlined" class="app-status-panel--warning app-status-text-warning">
              <span class="text-xs whitespace-nowrap">{{t "lpn.no_associated_school"}}</span>
            </Chip>
          {{/if}}
          {{#if this.mergedLpn.employerRecommended}}
            <div class="tio-row-flush text-md font-semibold">
              <PhSealCheck @size="20" class="tio-fg-muted" @weight="bold" />
              <span>{{t "lpn.employer_recommended"}}</span>
            </div>
          {{/if}}
          {{#if this.topBadge}}
            <div class="tio-row-flush text-md font-semibold">
              <PhCurrencyCircleDollar @size="20" class="tio-fg-muted" @weight="bold" />
              <span>{{this.topBadge}}</span>
            </div>
          {{/if}}
        </div>

        <h3 class="mt-1 tio-text-xl-semibold tio-fg-primary">{{this.mergedLpn.schoolName}}</h3>

        <div class="min-h-[14px]">
          {{#if this.providedBy}}
            <p class="tio-text-caption font-semibold uppercase tracking-wide">
              {{t "lpn.provided_by"}}
              <span class="tio-fg-secondary">{{this.providedBy}}</span>
            </p>
          {{/if}}
        </div>

        <div class="mt-1 min-h-[84px] tio-text-sm-normal tio-fg-secondary prose prose-sm">
          {{this.descriptionHtml}}
        </div>

        <div class="mt-3 min-h-[20px]">
          {{#if this.tags.length}}
            <div class="flex flex-wrap gap-2">
              {{#each this.tags as |tag|}}
                <Chip @appearance="outlined" class="tio-bg-sunken tio-fg-secondary">
                  <span class="text-xs whitespace-nowrap">{{tag}}</span>
                </Chip>
              {{/each}}
            </div>
          {{/if}}
        </div>
      </VStack>
    </ConditionalLink>
    <Modal
      @isOpen={{this.isDeleteModalOpen}}
      @onClose={{this.closeDeleteModal}}
      @ariaLabel={{t "common.aria_labels.delete_partner_confirmation"}}
    >
      <:header>{{t "lpn.delete_partner_confirmation"}}</:header>
      <:body>
        {{t "invoices.permanent_action"}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteModal}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deletePartner}} @isRunning={{this.isDeleting}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>
  </template>
}
